import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api } from '@/lib/api';
import type { Envelope } from '@/lib/types';

/**
 * The unread-messages badge, kept fresh by polling — the chosen real-time model.
 * Only runs while signed in; App starts it on authentication and stops it on
 * sign-out. Polling pauses while the tab is hidden so a backgrounded app is quiet.
 */
const POLL_MS = 20_000;

export const useMessagesStore = defineStore('messages', () => {
  const unread = ref(0);
  let timer: number | null = null;

  async function refresh(): Promise<void> {
    try {
      unread.value = (await api.get<Envelope<{ unread: number }>>('/messages/unread-count')).data.unread;
    } catch {
      // A failed poll (offline, 401 mid-logout) leaves the last known count.
    }
  }

  function start(): void {
    if (timer != null) return;
    void refresh();
    timer = window.setInterval(() => {
      if (!document.hidden) void refresh();
    }, POLL_MS);
  }

  function stop(): void {
    if (timer != null) {
      clearInterval(timer);
      timer = null;
    }
    unread.value = 0;
  }

  return { unread, refresh, start, stop };
});
